/**
 * useConfigStore — live projection of the server-generated `config/public` doc.
 *
 * SHARED CONTRACT (runtime business config):
 *   - init(uid)  → onSnapshot(doc 'config/public'); called by AuthGate after login.
 *   - publicConfig — null until the first snapshot arrives (or when the doc is
 *     absent). NULL MEANS "USE CODE DEFAULTS": every consumer overlays this config
 *     on top of its existing literals, so first paint + offline behave EXACTLY
 *     like today.
 *   - cleanup()  → unsubscribe + reset; called on signout.
 *
 * The doc shape (projection, never contains server-only keys):
 *   { version, updatedAt, moduleCatalog, billing:{tiers,founderMinAmount,creditPacks},
 *     ai:{allowedModelTiers,clientDefaults} }
 */
import { create } from 'zustand';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import * as moduleCatalog from '../config/moduleCatalog';
import * as styleCatalog from '../config/styleCatalog';

const useConfigStore = create((set, get) => ({
    publicConfig: null,
    _unsub: null,

    init(uid) {
        if (!uid) return;
        // Re-init: drop any previous listener first (but keep last config until
        // the new snapshot lands — avoids a defaults flash on account switch).
        const prev = get()._unsub;
        if (prev) prev();

        const ref = doc(db, 'config', 'public');
        const unsub = onSnapshot(
            ref,
            (snap) => {
                set({ publicConfig: snap.exists() ? snap.data() : null });
            },
            () => {
                // Transient listener errors: keep the last known config so
                // offline never regresses below what we already have.
            }
        );
        set({ _unsub: unsub });
    },

    cleanup() {
        const { _unsub } = get();
        if (_unsub) _unsub();
        set({ publicConfig: null, _unsub: null });
    },
}));

// Activate the moduleCatalog runtime overlay. Registration (not a direct import
// from moduleCatalog) keeps the catalog dependency-free so isolated tests that
// import only the catalog see pure code defaults. Guarded so partial test mocks
// of moduleCatalog without this hook can never crash the store at import time.
if (typeof moduleCatalog.__setPublicConfigGetter === 'function') {
    moduleCatalog.__setPublicConfigGetter(() => useConfigStore.getState().publicConfig);
}
if (typeof styleCatalog.__setPublicConfigGetter === 'function') {
    styleCatalog.__setPublicConfigGetter(() => useConfigStore.getState().publicConfig);
}

export default useConfigStore;
